import React, { useEffect, useRef } from "react";
import RichText from "components/rich-text";
import RenderAccordions from "./accordions";
import QuantityInput from "components/quantity-input";
import WithCollaboration from "./with-collaboration";
import ReviewsWidget from "utils/reviews";
import { useSelector } from "react-redux";
import ProductPrices from "../prices";
import { CURRENT_COUNTRY } from "utils/constants";
import loadDetailReviewsWidget from "services/load-detail-reviews-widget";
import { useGlobalContext } from "context/global";
import SubscriptionQuantityInput from "components/subs-quantity-input";
import extractIdFromGid from "helpers/extract-gid";
import PricePerKg from "components/price-per-kg";

const ProductDetails = ({ product, closeModal, hideATCPrice, lang }) => {
	const { accordions = [], nutrients = {}, tags = [], description = [], collaboration } = product.details ?? {};
	const { translation = {}, language = lang, isSubscriptionPage } = useGlobalContext();
	const { activeBox } = useSelector((state) => state.cart);
	const { subscriptionId } = useSelector(state => state.account.subscription);
	const { reviewCount } = product?.reviewAggregate || {};
	const reviewsRef = useRef(null);

	let price = product?.variant?.price;

	if (product?.variant?.weight && activeBox?.discount > 0)
		price -= activeBox.discount;

	// In BNL, some SKUs have an "A" added at the end due to packaging changes. 
	// We remove this suffix to ensure synchronization with DE SKUs.
	const modifiedSku = product?.variant?.sku?.endsWith("A") && !product?.variant?.sku?.endsWith(" A") ?
		product.variant.sku.slice(0, -1) : product?.variant?.sku;

	useEffect(() => {
		loadDetailReviewsWidget(modifiedSku, language === "default" ? CURRENT_COUNTRY : language);
	}, [language]);

	const scrollToBottom = () => {
		if (reviewsRef.current) {
			reviewsRef.current.scrollIntoView({ behavior: "smooth" });
		}
	};

	return (
		<div className="px-7 md:px-14">
			{!hideATCPrice &&
				<div className="flex flex-col mt-5 text-center">
					<div className="flex justify-center gap-2 items-center text-lg">
						<ProductPrices className="text-lg" variant={product.variant} />
						{product.details?.weight ? ` / ${product.details?.weight}g` : ""}
					</div>
					<PricePerKg price={price} weight={product.details?.weight} />
				</div>}
			{!hideATCPrice &&
				<div className="my-5 w-1/2 mx-auto">
					{isSubscriptionPage && subscriptionId ?
						<SubscriptionQuantityInput
							product={product}
							stock={product.variant.stock}
							productVariantId={extractIdFromGid(product.variant.id)}
							onToastClick={closeModal}
						/> : <QuantityInput product={product} newLayout />}
				</div>
			}

			<div onClick={scrollToBottom} role="button" tabIndex={0}>
				<ReviewsWidget sku={modifiedSku} position="center" isDetailsView />
			</div>

			<div className="text-center">
				<RichText value={description} />
			</div>

			{tags.length > 0 && (
				<div className="flex flex-row justify-center py-2.5 px-4 flex-wrap my-5">
					{tags.map((tag, idx) => (
						<span className="tag" key={idx}>
							{tag}
						</span>
					))}
				</div>
			)}

			{collaboration && <WithCollaboration collaboration={collaboration} />}

			<div className="flex flex-row justify-between md:pt-6 pb-4 px-4">
				{Object.keys(nutrients).filter(k => k !== "_type")
					.map(key => (
						<div key={key} className="flex flex-col text-center w-[22%]">
							<span className="text-small uppercase text-secondary-darker tracking-widest">{key}</span>
							<span className="text-body mt-1.5">{nutrients[key]}</span>
						</div>
					))}
			</div>

			<div className="pb-5 pt-2.5">
				<RenderAccordions accordions={accordions} />
			</div>
			<div ref={reviewsRef} className="w-full text-center product-reviews-details">
				{reviewCount > 0 && <h2 className="py-4">{translation.other?.reviews}</h2>}
				<div id="ReviewsWidget"></div>
			</div>
		</div>
	);
};

export default ProductDetails;
